/**
 * Which motor cover a priced option is -- the frontend half of
 * `backend/apps/quotes/cover_types.py`.
 *
 * One insurer can price the same car three ways (GoDigit 20101 / 20102 /
 * 20103), and the three rows are otherwise identical: same insurer, same
 * request, same product. The server records the cover on each option as
 * `cover_type` and sends a `cover_label` beside it; this module adds the one
 * line a customer needs to tell them apart, and the display order.
 *
 * A row whose cover was never recorded (a premium typed in by hand) has
 * `cover_type: null`, and nothing here guesses one for it.
 */

export type CoverType = 'comprehensive' | 'tp_only' | 'saod';

export const COVER_ORDER: readonly CoverType[] = ['comprehensive', 'tp_only', 'saod'];

export const COVER_LABELS: Record<CoverType, string> = {
  comprehensive: 'Comprehensive',
  tp_only: 'Third-Party only',
  saod: 'Own-Damage only',
};

export const COVER_DESCRIPTIONS: Record<CoverType, string> = {
  comprehensive: 'Covers damage to your own vehicle and your legal liability to others.',
  tp_only:
    'Covers only your legal liability to other people and their property -- the legal minimum.',
  saod: 'Covers damage to your own vehicle only; needs a separate third-party policy that is still active.',
};

function isCoverType(value: unknown): value is CoverType {
  return typeof value === 'string' && (COVER_ORDER as readonly string[]).includes(value);
}

/** The label to show, preferring the server's own when it sent one. */
export function coverLabel(option: { cover_type?: string | null; cover_label?: string | null }): string | null {
  if (option.cover_label) return option.cover_label;
  return isCoverType(option.cover_type) ? COVER_LABELS[option.cover_type] : null;
}

export function coverDescription(coverType: string | null | undefined): string | null {
  return isCoverType(coverType) ? COVER_DESCRIPTIONS[coverType] : null;
}

/** Comprehensive, then Third-Party only, then Own-Damage only; unrecorded last.
 *  Stable, so the server's own order (cheapest first) holds within a cover. */
export function sortByCover<T extends { cover_type?: string | null }>(options: readonly T[]): T[] {
  const rank = (o: T) => {
    const index = isCoverType(o.cover_type) ? COVER_ORDER.indexOf(o.cover_type) : -1;
    return index === -1 ? COVER_ORDER.length : index;
  };
  return options
    .map((option, position) => ({ option, position }))
    .sort((a, b) => rank(a.option) - rank(b.option) || a.position - b.position)
    .map(({ option }) => option);
}

/** An option's premium as a number: the total, else the gross; null when the
 *  option carries no usable figure. Never estimated. */
export function optionPremium(option: {
  premium_total?: string | number | null;
  premium_gross?: string | number | null;
}): number | null {
  const raw = option.premium_total ?? option.premium_gross ?? null;
  if (raw === null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}
